import { computed } from 'vue'
import { useTransformLearningStore } from '../stores/TransformLearningStore'
import { htmlToPlainText } from '../utils/sanitizeHtml'

/**
 * Shared source for the Transform Learning search typeahead. Both the dashboard /
 * category-detail search boxes and the dedicated results page search the whole
 * catalogue with the same "Lesson N: Title" / "Quiz N: Title" display format, so the
 * item lists are derived here once instead of being copied into each view.
 *
 * - `searchItems`      compact list ({ id, displayTitle, description }) for the typeahead.
 * - `searchableLessons` enriched list (full lesson + displayTitle + description) for the
 *   results grid, which renders the matched lessons directly.
 *
 * Both are position-ordered (module position → lesson position) so suggestions and
 * results appear in the same order they do everywhere else in the module.
 */
export function useTransformSearch() {
  const store = useTransformLearningStore()

  const sortedModules = computed(
    () => [...store.modules].sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
  )

  function displayTitleFor(lesson) {
    const prefix = lesson.lessonType === 'quiz' ? 'Quiz' : 'Lesson'
    const number = lesson.displayNumber ?? 1
    return `${prefix} ${number}: ${lesson.title}`
  }

  const searchableLessons = computed(() => {
    const out = []
    for (const mod of sortedModules.value) {
      const detail = store.categoryDetail(mod.id)
      if (!detail) continue
      for (const lesson of detail.lessons) {
        out.push({
          ...lesson,
          displayTitle: displayTitleFor(lesson),
          description: htmlToPlainText(lesson.bodyHtml || lesson.descriptionHtml || ''),
        })
      }
    }
    return out
  })

  const searchItems = computed(() =>
    searchableLessons.value.map(l => ({
      id: l.id,
      displayTitle: l.displayTitle,
      description: l.description,
    }))
  )

  return { searchItems, searchableLessons }
}
